import { ClipboardDocumentListIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { EditorTabs, tabPanelId } from './EditorTabs.tsx';
import type { GlAccount } from '../../../hooks/useGLAccounts.ts';
import { BookingProvider, type RecategorizeCategoryType } from '../../../types/enums.ts';
import { DocumentPreview } from './DocumentPreview';
import { CreditCardPreview } from './CreditCardPreview.tsx';
import { ArchivedDocumentView } from './ArchivedDocumentView.tsx';
import { RecategorisationView } from './RecategorisationView.tsx';
import { PendingNotice } from './PendingNotice.tsx';
import { MatchOverview } from './MatchOverview.tsx';
import type { TFICDocument, MatchBillPart } from '../types/Document';
import type { MatchData } from '../types/data.ts';
import type { LineItemNeedingAttention } from '../types/LineItem.ts';
import type { DocumentSavePayload } from '../utils/documentSave.ts';
import type { SaveOptions } from '../hooks/useDocumentDrawerService.ts';
import type { MatchTransactionPart } from '../../../pages/app/partner/pages/partnerUnmatchedTransactionsPage/types.ts';
import { DocumentDrawerDisplayMode } from '../types/drawer.ts';
import type { BillTodo } from '../types/billTodo.ts';
import type { BillTodoType } from '../../../generated/enums.ts';
import { DocumentEditor } from './DocumentEditor.tsx';
import { EditorSkeleton, PreviewSkeleton } from './EditorSkeleton.tsx';
import { DocumentWorkspace } from './DocumentWorkspace.tsx';
import { cleanDocumentFileName } from '../../../lib/TFICLib.ts';
import { BillFixedAssetChip } from './BillFixedAssetChip.tsx';
import { BillFixedAssetDialogs, type FixedAssetPopup } from './BillFixedAssetDialogs.tsx';
import { shouldShowFixedAssetPanel } from '../utils/fixedAssetPanelRules.ts';

type DocumentDrawerViewProps = {
    document: TFICDocument | null;
    isDocumentLoading: boolean;
    isDocumentContentLoading: boolean;
    documentId: string;
    businessId: string;
    documentLoadError: string | null;
    matchData: MatchData | null;
    glAccounts: GlAccount[];
    glAccountsLoading: boolean;
    lineItemsNeedingAttention: LineItemNeedingAttention[];
    initialTab?: number;
    displayMode?: DocumentDrawerDisplayMode;
    onSave: (payload: DocumentSavePayload, options?: SaveOptions) => Promise<unknown>;
    onConvertToEuro?: () => Promise<void>;
    onSaveAndNext?: (payload: DocumentSavePayload) => Promise<void>;
    saveAndNextOnly?: boolean;
    onUnmatch: (
        matchId: string,
        bills: MatchBillPart[],
        transactions: MatchTransactionPart[],
    ) => void | Promise<void>;
    onRecategorize: (category: RecategorizeCategoryType) => Promise<void>;
    onDelete: () => Promise<void>;
    canArchive: boolean;
    setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
    saveNudge?: number;
    billTodos: BillTodo[];
    onDismissTodo: (todoType: BillTodoType) => Promise<void>;
    onShowDuplicates: () => void;
    hasUnsavedChanges: boolean;
    canLinkFixedAssets: boolean;
};

const MATCHES_TAB_INDEX = 1;
const FIELDS_TAB = 'fields';
const MATCHES_TAB = 'matches';

const CARD_CLASS = 'flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm';

function Card({ children }: { children: React.ReactNode }) {
    return <div className={CARD_CLASS}>{children}</div>;
}

function SplitLayout({
    left,
    right,
    document,
}: {
    left: React.ReactNode;
    right: React.ReactNode;
    document?: Pick<TFICDocument, 'file_name' | 'mimetype'> | null;
}) {
    return (
        <Card>
            <DocumentWorkspace
                editor={left}
                preview={right}
                fileName={document?.file_name ? cleanDocumentFileName(document.file_name) : undefined}
                mimeType={document?.mimetype}
            />
        </Card>
    );
}

function PreviewPane({ document, isDocumentContentLoading, documentLoadError }: {
    document: TFICDocument;
    isDocumentContentLoading: boolean;
    documentLoadError: string | null;
}) {
    if (isDocumentContentLoading) {
        return <PreviewSkeleton />;
    }
    const message = documentLoadError ?? (!document.file_blob ? 'Het documentbestand kon niet worden geladen.' : null);
    if (message) {
        return (
            <div role="status" className="flex h-full items-center justify-center p-6 text-center text-[13px] text-gray-600">
                <div className="max-w-xs rounded-xl border border-gray-200 bg-white px-6 py-8 shadow-sm">
                    <p className="font-medium text-gray-800">Voorbeeld niet beschikbaar</p>
                    <p className="mt-1 text-xs leading-5 text-gray-500">{message}</p>
                </div>
            </div>
        );
    }
    return (
        <div className="h-full min-h-0 w-full">
            <DocumentPreview document={document} />
        </div>
    );
}

export function DocumentDrawerView({
    document,
    isDocumentLoading,
    isDocumentContentLoading,
    documentId,
    businessId,
    documentLoadError,
    matchData,
    glAccounts,
    glAccountsLoading,
    lineItemsNeedingAttention,
    initialTab,
    displayMode = DocumentDrawerDisplayMode.FollowReason,
    onSave,
    onConvertToEuro,
    onSaveAndNext,
    saveAndNextOnly,
    onUnmatch,
    onRecategorize,
    onDelete,
    canArchive,
    setHasUnsavedChanges,
    saveNudge = 0,
    billTodos,
    onDismissTodo,
    onShowDuplicates,
    hasUnsavedChanges,
    canLinkFixedAssets,
}: DocumentDrawerViewProps) {
    const [searchParams] = useSearchParams();
    const isDocumentMatched =
        !!matchData && (matchData.bills.length > 0 || matchData.transactions.length > 0);
    const shouldOpenMatchesTab = searchParams.get('matchesTab') === 'true';
    const requestedTab = initialTab ?? (shouldOpenMatchesTab ? MATCHES_TAB_INDEX : 0);
    const defaultActiveTab = requestedTab === MATCHES_TAB_INDEX && isDocumentMatched ? MATCHES_TAB : FIELDS_TAB;
    const tabResetKey = `${documentId}:${defaultActiveTab}`;
    const [tabSelection, setTabSelection] = useState({ key: tabResetKey, active: defaultActiveTab });
    if (tabSelection.key !== tabResetKey) {
        setTabSelection({ key: tabResetKey, active: defaultActiveTab });
    }
    const activeTab = tabSelection.key === tabResetKey ? tabSelection.active : defaultActiveTab;
    const setActiveTab = (active: string) => setTabSelection({ key: tabResetKey, active });
    const [fixedAssetPopup, setFixedAssetPopup] = useState<{ documentId: string; popup: FixedAssetPopup }>(
        { documentId, popup: null },
    );
    const openFixedAssetPopup = fixedAssetPopup.documentId === documentId ? fixedAssetPopup.popup : null;
    const showFixedAssetPopup = (popup: FixedAssetPopup) => setFixedAssetPopup({ documentId, popup });
    const followReason = displayMode === DocumentDrawerDisplayMode.FollowReason;

    if (isDocumentLoading) {
        return <SplitLayout left={<EditorSkeleton />} right={<PreviewSkeleton />} />;
    }

    if (!document) {
        return (
            <Card>
                <div className="flex items-center justify-center h-full text-[13px] text-gray-600">
                    {documentLoadError ?? 'No document selected'}
                </div>
            </Card>
        );
    }

    if (document.document_category === 'credit_card_statement') {
        return <Card><CreditCardPreview document={document} /></Card>;
    }

    if (document.document_category === 'archive') {
        return <Card><ArchivedDocumentView document={document} isContentLoading={isDocumentContentLoading} /></Card>;
    }

    if (followReason && document.reason === 'awaiting_scan') {
        return <Card><PendingNotice document={document} /></Card>;
    }

    if (followReason && document.reason === 'needs_category') {
        return (
            <Card>
                <RecategorisationView
                    document={document}
                    onRecategorize={onRecategorize}
                    onDelete={onDelete}
                    canArchive={canArchive}
                />
            </Card>
        );
    }

    const showsFixedAssets = shouldShowFixedAssetPanel(canLinkFixedAssets, document) && !!document.editable_bill;

    const editor = (
        <DocumentEditor
            key={documentId}
            document={document}
            bookingProvider={BookingProvider.DEFAULT}
            isDocumentLoading={isDocumentLoading}
            documentId={documentId}
            businessId={businessId}
            glAccounts={glAccounts}
            lineItemsNeedingAttention={lineItemsNeedingAttention}
            onSave={onSave}
            onConvertToEuro={onConvertToEuro}
            onSaveAndNext={onSaveAndNext}
            saveAndNextOnly={saveAndNextOnly}
            setHasUnsavedChanges={setHasUnsavedChanges}
            saveNudge={saveNudge}
            billTodos={billTodos}
            onDismissTodo={onDismissTodo}
            onShowDuplicates={onShowDuplicates}
            documentBarStart={showsFixedAssets ? (
                <BillFixedAssetChip
                    linkedFixedAsset={document.fixed_asset}
                    hasUnsavedChanges={hasUnsavedChanges}
                    onAdd={() => showFixedAssetPopup('choose')}
                    onPreview={() => showFixedAssetPopup('preview')}
                    onUnlink={() => showFixedAssetPopup('unlink')}
                />
            ) : undefined}
        />
    );

    const fixedAssetDialogs = showsFixedAssets ? (
        <BillFixedAssetDialogs
            document={document}
            businessId={businessId}
            documentId={documentId}
            glAccounts={glAccounts}
            glAccountsLoading={glAccountsLoading}
            openPopup={openFixedAssetPopup}
            onOpenPopup={showFixedAssetPopup}
        />
    ) : null;

    return (
        <SplitLayout
            document={document}
            left={
                <>
                {fixedAssetDialogs}
                {isDocumentMatched ? (
                    <>
                        <EditorTabs
                            tabs={[
                                { id: FIELDS_TAB, label: 'Velden', icon: PencilSquareIcon },
                                { id: MATCHES_TAB, label: 'Matches', icon: ClipboardDocumentListIcon },
                            ]}
                            activeId={activeTab}
                            onChange={setActiveTab}
                        />
                        {activeTab === FIELDS_TAB ? (
                            <div id={tabPanelId(FIELDS_TAB)} role="tabpanel" aria-labelledby={`editor-tab-${FIELDS_TAB}`} className="flex min-h-0 flex-1 flex-col">
                                {editor}
                            </div>
                        ) : (
                            <div id={tabPanelId(MATCHES_TAB)} role="tabpanel" aria-labelledby={`editor-tab-${MATCHES_TAB}`} className="min-h-0 flex-1 overflow-y-auto p-4">
                                <MatchOverview data={matchData} currentDocumentId={documentId} onUnmatch={onUnmatch} />
                            </div>
                        )}
                    </>
                ) : (
                    editor
                )}
                </>
            }
            right={<PreviewPane document={document} isDocumentContentLoading={isDocumentContentLoading} documentLoadError={documentLoadError} />}
        />
    );
}
